import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useFavorites } from "@/hooks/useFavorites";
import { getMovieById, getRelated } from "@/utils/movieUtils";
import { themeCollections } from "@/mocks/movies";
import RelatedList from "./components/RelatedList";

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const movie = useMemo(() => (id ? getMovieById(id) : undefined), [id]);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState("");

  usePageMeta({
    title: movie ? `${movie.title}详情 - 伦理影院` : "影视详情 - 伦理影院",
    description: movie
      ? `${movie.title}（${movie.year}）剧情简介与演职员信息，主演：${movie.cast.join("、")}，导演：${movie.director}。${movie.synopsis.slice(0, 60)}……`
      : "影视详情页",
    keywords: movie ? `${movie.title},伦理影院,伦理影视,${movie.tags.join(",")}` : "伦理影院",
  });

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(""), 2200);
    return () => window.clearTimeout(timer);
  }, [toast]);

  if (!movie) {
    return (
      <div className="flex min-h-screen flex-col bg-background-50">
        <SiteHeader />
        <main className="flex flex-1 flex-col items-center justify-center px-4 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-file-unknow-line text-3xl"></i>
          </span>
          <h1 className="mt-5 font-heading text-xl font-bold text-foreground-950">未找到该影视内容</h1>
          <p className="mt-2 text-sm text-foreground-600">影片可能已下线或链接有误，去分类页继续浏览吧。</p>
          <Link
            to="/category?type=movie"
            className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            返回分类页
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const fav = isFavorite(movie.id);
  const related = getRelated(movie, 6);
  const theme = themeCollections.find((t) => t.key === movie.theme);

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setToast("链接已复制，快分享给朋友吧");
    } catch {
      setToast("复制失败，可手动复制浏览器地址");
    }
  };

  const handleFavorite = () => {
    const now = toggleFavorite(movie.id);
    setToast(now ? "已加入收藏" : "已取消收藏");
  };

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />

      {movie.backdrop && (
        <div className="relative h-56 w-full overflow-hidden md:h-72">
          <img src={movie.backdrop} alt={`${movie.title} 剧照`} title={`${movie.title} 伦理影视`} className="h-full w-full object-top" />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-primary-950/40 to-primary-950/20"></div>
        </div>
      )}

      <main className="flex-1 w-full px-4 md:px-8">
        <nav aria-label="面包屑" className={`text-sm text-foreground-500 ${movie.backdrop ? "-mt-6 relative z-10" : "pt-6"}`}>
          <ol className="flex flex-wrap items-center gap-2">
            <li>
              <Link to="/" className="transition-colors hover:text-primary-600">
                首页
              </Link>
            </li>
            <li>/</li>
            <li>
              <Link to={`/category?type=${movie.type}`} className="transition-colors hover:text-primary-600">
                {movie.type === "movie" ? "伦理电影" : "伦理电视剧"}
              </Link>
            </li>
            <li>/</li>
            <li className="text-foreground-800">{movie.title}</li>
          </ol>
        </nav>

        <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <div className="flex flex-col gap-6 sm:flex-row">
              <div className="h-72 w-48 shrink-0 overflow-hidden rounded-lg bg-background-200 sm:h-80 sm:w-56">
                <img
                  src={movie.cover}
                  alt={`${movie.title} 高清封面`}
                  title={`${movie.title} 伦理影视`}
                  className="h-full w-full object-top"
                />
              </div>

              <div className="min-w-0 flex-1">
                <h1 className="font-heading text-3xl font-bold text-foreground-950">{movie.title}</h1>
                <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-foreground-600">
                  <span className="flex items-center gap-1 rounded-md bg-accent-100 px-2 py-0.5 font-semibold text-accent-900">
                    <i className="ri-star-fill text-accent-500"></i> {movie.rating.toFixed(1)} 分
                  </span>
                  <span>{movie.year}</span>
                  <span>{movie.region}</span>
                  <span>
                    {movie.type === "movie" ? `${movie.duration} 分钟` : `共 ${movie.episodes} 集 · 单集约 ${movie.duration} 分钟`}
                  </span>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  {movie.tags.map((tag) => (
                    <Link
                      key={tag}
                      to={`/search?q=${encodeURIComponent(tag)}`}
                      className="whitespace-nowrap rounded-full border border-background-300/60 bg-background-100 px-3 py-1 text-xs text-foreground-700 transition-colors hover:border-accent-300 hover:text-accent-600"
                    >
                      {tag}
                    </Link>
                  ))}
                  {theme && (
                    <Link
                      to={`/category?type=${movie.type}&theme=${theme.key}`}
                      className="whitespace-nowrap rounded-full border border-primary-200 bg-primary-50 px-3 py-1 text-xs text-primary-700 transition-colors hover:border-primary-400"
                    >
                      专题 · {theme.name}
                    </Link>
                  )}
                </div>

                <dl className="mt-5 grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
                  <div className="flex gap-2">
                    <dt className="shrink-0 text-foreground-500">导演</dt>
                    <dd className="text-foreground-900">{movie.director}</dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="shrink-0 text-foreground-500">主演</dt>
                    <dd className="text-foreground-900">{movie.cast.join("、")}</dd>
                  </div>
                </dl>

                <div className="mt-6 flex flex-wrap items-center gap-3">
                  <Link
                    to={`/play/${movie.id}`}
                    className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                  >
                    <i className="ri-play-fill text-base"></i> 立即播放
                  </Link>
                  <button
                    type="button"
                    onClick={handleFavorite}
                    className={`inline-flex items-center gap-2 whitespace-nowrap rounded-md border px-5 py-3 text-sm font-semibold transition-colors ${
                      fav
                        ? "border-accent-400 bg-accent-100 text-accent-900"
                        : "border-background-300/60 text-foreground-700 hover:border-accent-300 hover:text-accent-600"
                    }`}
                  >
                    <i className={fav ? "ri-heart-fill" : "ri-heart-line"}></i>
                    {fav ? "已收藏" : "收藏"}
                  </button>
                  <button
                    type="button"
                    onClick={handleShare}
                    className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300/60 px-5 py-3 text-sm font-semibold text-foreground-700 transition-colors hover:border-secondary-300 hover:text-secondary-700"
                  >
                    <i className="ri-share-line"></i> 分享
                  </button>
                </div>
              </div>
            </div>

            <section className="mt-10">
              <h2 className="font-heading text-xl font-bold text-foreground-950">剧情简介</h2>
              <p className="mt-4 text-sm leading-7 text-foreground-700">{movie.synopsis}</p>
              <p className="mt-4 text-sm leading-7 text-foreground-700">
                《{movie.title}》以扎实的叙事和细腻的人物刻画，将伦理困境层层铺开。影片没有简单地给出是非对错的结论，
                而是把选择的重量交还给每一个角色，也交还给观众。{movie.director}导演以克制的镜头语言，
                让{movie.cast.slice(0, 2).join("与")}等演员的表演显得格外真实，值得细细品味。
              </p>
            </section>

            {movie.type === "tv" && (
              <section className="mt-10">
                <h2 className="font-heading text-xl font-bold text-foreground-950">剧集列表</h2>
                <div className="mt-4 grid grid-cols-4 gap-3 sm:grid-cols-6 md:grid-cols-8">
                  {Array.from({ length: Math.min(movie.episodes, 24) }, (_, i) => i + 1).map((ep) => (
                    <Link
                      key={ep}
                      to={`/play/${movie.id}?ep=${ep}`}
                      className="flex h-10 items-center justify-center rounded-md border border-background-300/60 bg-background-100 text-sm text-foreground-700 transition-colors hover:border-primary-400 hover:bg-primary-50 hover:text-primary-700"
                    >
                      第{ep}集
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>

          <aside className="lg:col-span-1">
            <RelatedList movies={related} />
          </aside>
        </div>
      </main>

      {toast && (
        <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-md bg-foreground-950 px-5 py-3 text-sm text-background-50 shadow-lg">
          {toast}
        </div>
      )}

      <SiteFooter />
    </div>
  );
}